import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CustomColorPickerInput } from "@air/component-custom-color-picker-input";
import { Close, MiscColorWheelIcon, Plus } from "@air/next-icons";
import { Chip } from "@air/primitive-chip";
import { IconButton } from "@air/primitive-icon-button";
import classNames from "classnames";
import { forwardRef, memo, useCallback, useImperativeHandle, useState } from "react";
export const CustomColorPickerControl = /*#__PURE__*/ memo(/*#__PURE__*/ forwardRef((param, ref)=>{
    let { onColorSelect, onRemoveHex, selectedColors } = param;
    const [savedHexes, setSavedHexes] = useState([]);
    const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
    // Expose method to clear saved hexes
    useImperativeHandle(ref, ()=>({
            clearSavedHexes: ()=>{
                // Instead of removing the chips, just deselect them in Redux
                savedHexes.forEach((hex)=>{
                    if (selectedColors.includes(hex)) {
                        onColorSelect({
                            color: {
                                hex,
                                name: hex
                            },
                            isSelected: false
                        });
                    }
                });
            }
        }));
    const handleRemoveHex = useCallback((hexToRemove)=>{
        setSavedHexes((prev)=>prev.filter((hex)=>hex !== hexToRemove));
        onColorSelect({
            color: {
                hex: hexToRemove,
                name: hexToRemove
            },
            isSelected: false
        });
        onRemoveHex(hexToRemove);
    }, [
        onColorSelect,
        onRemoveHex
    ]);
    const onSaveCustomColor = useCallback((hex)=>{
        // Only add the hex if it doesn't already exist in savedHexes
        if (!savedHexes.includes(hex)) {
            setSavedHexes((prev)=>[
                    ...prev,
                    hex
                ]);
        }
        // Always select the color, even if it was already saved but not selected
        onColorSelect({
            color: {
                hex,
                name: hex
            },
            isSelected: true
        });
        setIsColorPickerOpen(false);
    }, [
        onColorSelect,
        savedHexes
    ]);
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": "CUSTOM_COLOR_PICKER_CONTROL",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col items-start gap-3",
            children: [
                /*#__PURE__*/ _jsx("p", {
                    className: "text-12 text-grey-12",
                    children: "More colors"
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "flex w-full flex-col gap-2",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap gap-3",
                        children: [
                            savedHexes.length > 0 && savedHexes.map((hex)=>{
                                const isSelected = selectedColors.includes(hex);
                                return /*#__PURE__*/ _jsx(Chip, {
                                    className: classNames("cursor-pointer rounded-3xl border-2 px-1 py-3", {
                                        "border-grey-8": isSelected,
                                        "border-grey-5": !isSelected
                                    }),
                                    onClick: ()=>{
                                        onColorSelect({
                                            color: {
                                                hex,
                                                name: hex
                                            },
                                            isSelected: !isSelected
                                        });
                                    },
                                    prefix: /*#__PURE__*/ _jsx("div", {
                                        className: "mr-1 size-4 rounded-full",
                                        style: {
                                            backgroundColor: hex
                                        }
                                    }),
                                    suffix: /*#__PURE__*/ _jsx(IconButton, {
                                        onClick: ()=>{
                                            handleRemoveHex(hex);
                                        },
                                        icon: Close,
                                        label: "Remove ".concat(hex),
                                        appearance: "ghost",
                                        className: "ml-1",
                                        color: "grey",
                                        "data-testid": "CUSTOM_COLOR_REMOVE_BUTTON",
                                        "data-hex": hex
                                    }),
                                    asChild: true,
                                    "data-testid": "CUSTOM_COLOR_CHIP",
                                    "data-hex": hex,
                                    children: /*#__PURE__*/ _jsx("div", {
                                        className: "flex h-5 items-center",
                                        children: /*#__PURE__*/ _jsx("span", {
                                            className: classNames("text-12", {
                                                "font-semibold": isSelected
                                            }),
                                            children: hex
                                        })
                                    })
                                }, hex);
                            }),
                            /*#__PURE__*/ _jsx(Chip, {
                                className: "cursor-pointer rounded-3xl border-2 p-3",
                                onClick: ()=>setIsColorPickerOpen((prev)=>!prev),
                                asChild: true,
                                role: "button",
                                tabIndex: 0,
                                "data-testid": "CUSTOM_COLOR_PICKER_BUTTON",
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(MiscColorWheelIcon, {
                                            className: "size-5"
                                        }),
                                        /*#__PURE__*/ _jsx(Plus, {
                                            className: "size-3"
                                        })
                                    ]
                                })
                            })
                        ]
                    })
                }),
                isColorPickerOpen && /*#__PURE__*/ _jsxs("div", {
                    className: "relative flex w-full flex-col items-start gap-3 rounded-8 border border-grey-5 px-4 py-3 pb-4",
                    children: [
                        /*#__PURE__*/ _jsx(IconButton, {
                            icon: Close,
                            label: "Close",
                            appearance: "ghost",
                            color: "grey",
                            className: "absolute right-4 top-4 size-3",
                            onClick: ()=>setIsColorPickerOpen(false)
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-12 font-semibold text-grey-12",
                            "data-testid": "CUSTOM_COLOR_PICKER_LABEL",
                            children: "Custom color"
                        }),
                        /*#__PURE__*/ _jsx(CustomColorPickerInput, {
                            onSave: onSaveCustomColor
                        })
                    ]
                })
            ]
        })
    });
}));
CustomColorPickerControl.displayName = "CustomColorPickerControl";
